import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserService } from '../services/apiService';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const RecoverPasswordView: React.FC = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage('');

        try {
            await UserService.recoverPassword(email);
            setStatus('success');
        } catch (error: any) {
            setStatus('error');
            // Se il backend risponde con 404 o altro
            if (error.response && error.response.status === 404) {
                setErrorMessage('Nessun account associato a questa email.');
            } else {
                setErrorMessage('Si è verificato un errore. Riprova più tardi.');
            }
        }
    };

    if (status === 'success') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
                <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Controlla la tua email</h2>
                    <p className="text-gray-600 mb-6">
                        Abbiamo inviato un link di recupero a <strong>{email}</strong>.
                        Il link scadrà tra pochi minuti.
                    </p>
                    <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
                        Torna al login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                <Link to="/login" className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Torna indietro
                </Link>

                <h2 className="text-2xl font-bold text-gray-900 mb-2">Password dimenticata?</h2>
                <p className="text-gray-600 mb-6 text-sm">
                    Inserisci l'email associata al tuo account e ti invieremo le istruzioni per reimpostare la password.
                </p>

                {status === 'error' && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 text-sm rounded">
                        {errorMessage}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                            Email
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                placeholder="nome@azienda.it"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={status === 'loading'}
                        className={`w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white font-semibold bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ${
                            status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                    >
                        {status === 'loading' ? 'Invio in corso...' : 'Invia link di recupero'}
                    </button>
                </form>
            </div>
            <LegalFooter variant="public" className="fixed bottom-0 left-0 right-0" />
        </div>
    );
};

export default RecoverPasswordView;